import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Collapse, DatePicker, Form, Input, Modal, Switch, Tag, Typography } from 'antd'
import type { CollapseProps } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { CalendarOutlined, EditOutlined, ExclamationCircleOutlined, TagsOutlined, ToolOutlined } from '@ant-design/icons'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton, CardLoadingState, Pagination, ResultCard } from '@/widgets/demo/ListPrimitives'
import { computePagination, demoButtonClass } from '@/widgets/demo/listHelpers'
import { EmptyState, useToast } from '@/widgets/common'
import { releaseHistoriesApi } from '@/services/releaseHistories/api'
import type { ReleaseHistoryResponse } from '@/services/releaseHistories/types'
import {
  adminReleaseHistoriesApi,
  type ReleaseHistoryUpsert,
} from '@/services/admin/releaseHistoriesApi'
import { applyApiFieldErrors, getErrorMessage, maintenanceApi } from '@/services'
import { isHandledAuthError } from '@/services/client'
import { useAppNavigate, useAuth, useBackToParent, usePermission, usePersistentPageSize } from '@/hooks'

const { Text, Title } = Typography

interface FormValues {
  version: string
  title: string
  body: string
  releasedAt: Dayjs | null
}

function formatReleasedAt(value: string | null): string {
  if (!value) return '-'
  return value.replaceAll('-', '/')
}

function renderBody(body: string | null | undefined) {
  const normalized = body?.trim()
  if (!normalized) {
    return <Text type="secondary">更新内容は登録されていません。</Text>
  }

  return (
    <div className="whitespace-pre-wrap rounded-lg bg-gray-50 px-4 py-3 text-sm leading-7 text-gray-700">
      {normalized}
    </div>
  )
}

const requiredLabel = (label: string) => (
  <span className="inline-flex items-center gap-1">
    <span aria-hidden className="text-sm font-bold text-red-500">
      *
    </span>
    <span>{label}</span>
  </span>
)

const ReleaseHistory = () => {
  const goBackToParent = useBackToParent()
  const navigate = useAppNavigate()
  const { hasPermission } = usePermission()
  const { session } = useAuth()
  // 閲覧は全ユーザーに開放。作成・編集は RELEASE_HISTORY_VIEW（SYSTEM_ADMIN）のみ。
  const canManage = hasPermission('RELEASE_HISTORY_VIEW')
  // メンテナンスモードのスイッチは、ログイン ID に "deploy" を含むシステム管理者のみ表示する（顧客指摘#15）。
  const isDeployAdmin = !!session?.loginId && session.loginId.toLowerCase().includes('deploy')
  const toast = useToast()
  const [form] = Form.useForm<FormValues>()
  const [rows, setRows] = useState<ReleaseHistoryResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ReleaseHistoryResponse | null>(null)
  const [saving, setSaving] = useState(false)
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false)
  const [maintenanceSaving, setMaintenanceSaving] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistentPageSize('release-history')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await releaseHistoriesApi.list()
      setRows(data)
    } catch (err) {
      setRows([])
      if (isHandledAuthError(err)) return
      setError('リリース履歴の読み込みに失敗しました。')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // 更新履歴一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData()
  }, [fetchData])

  // deploy 管理者のみ、現在のメンテナンス状態を取得して表示する（公開ステータス API なので 403 にならない）。
  useEffect(() => {
    if (!isDeployAdmin) return
    let cancelled = false
    void maintenanceApi
      .status()
      .then(({ data }) => {
        if (!cancelled) setMaintenanceEnabled(Boolean(data.enabled))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [isDeployAdmin])

  const handleToggleMaintenance = (next: boolean) => {
    Modal.confirm({
      title: next ? 'メンテナンスモードを開始しますか？' : 'メンテナンスモードを解除しますか？',
      icon: <ExclamationCircleOutlined style={{ color: next ? '#cf1322' : '#1677ff' }} />,
      content: next
        ? 'メンテナンスを開始すると、deploy 管理者以外のすべてのユーザーはログインできなくなり、ログイン中のユーザーは強制的にログアウトされます。リリース作業の直前に実行してください。'
        : 'メンテナンスを解除すると、利用者は再びログインできるようになります。',
      okText: next ? 'メンテナンスを開始する' : '解除する',
      okButtonProps: { danger: next },
      cancelText: 'キャンセル',
      onOk: async () => {
        setMaintenanceSaving(true)
        try {
          const { data } = await maintenanceApi.setEnabled(next)
          setMaintenanceEnabled(Boolean(data.enabled))
          toast.success(
            next ? 'メンテナンスモードを開始しました。' : 'メンテナンスモードを解除しました。',
          )
        } catch (err) {
          if (!isHandledAuthError(err)) {
            toast.error(getErrorMessage(err, 'メンテナンスモードの切り替えに失敗しました。'))
          }
        } finally {
          setMaintenanceSaving(false)
        }
      },
    })
  }

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      version: '',
      title: '',
      body: '',
      releasedAt: dayjs(),
    })
    setModalOpen(true)
  }

  const openEdit = (row: ReleaseHistoryResponse) => {
    setEditing(row)
    form.resetFields()
    form.setFieldsValue({
      version: row.version,
      title: row.title,
      body: row.body,
      releasedAt: row.releasedAt ? dayjs(row.releasedAt) : dayjs(),
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    setEditing(null)
    form.resetFields()
  }

  const handleSubmit = async () => {
    let values: FormValues
    try {
      values = await form.validateFields()
    } catch (validationError: unknown) {
      if (validationError && typeof validationError === 'object' && 'errorFields' in validationError) return
      Modal.error({ content: '入力内容を確認してください。' })
      return
    }

    const payload: ReleaseHistoryUpsert = {
      version: values.version.trim(),
      title: values.title.trim(),
      body: values.body,
      releasedAt: (values.releasedAt ?? dayjs()).format('YYYY-MM-DD'),
    }

    setSaving(true)
    try {
      if (editing) {
        await adminReleaseHistoriesApi.update(editing.id, payload)
        toast.success('リリース履歴を更新しました。')
      } else {
        await adminReleaseHistoriesApi.create(payload)
        toast.success('リリース履歴を作成しました。')
      }
      setModalOpen(false)
      setEditing(null)
      form.resetFields()
      await fetchData()
    } catch (submitError) {
      if (isHandledAuthError(submitError)) return
      applyApiFieldErrors(form, submitError)
    } finally {
      setSaving(false)
    }
  }

  // 他の一覧画面と同じクライアント側ページネーション（表示件数は最大100件、localStorage に保存）。
  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(page, rows.length, pageSize)
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * pageSize, safePage * pageSize),
    [rows, safePage, pageSize],
  )

  const collapseItems = useMemo<CollapseProps['items']>(() => pageRows.map((item) => ({
    key: String(item.id),
    label: (
      <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {item.version ? (
              <Tag color="blue" className="!mr-0 inline-flex items-center gap-1">
                <TagsOutlined />
                {item.version}
              </Tag>
            ) : null}
            <Title level={5} className="!mb-0 !text-base">
              {item.title || 'リリース'}
            </Title>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Text type="secondary" className="text-sm">
            <CalendarOutlined className="mr-1" />
            {formatReleasedAt(item.releasedAt)}
          </Text>
          {canManage ? (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              className="!px-0"
              onClick={(e) => {
                e.stopPropagation()
                openEdit(item)
              }}
            >
              編集
            </Button>
          ) : null}
        </div>
      </div>
    ),
    children: renderBody(item.body),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [pageRows, canManage])

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={
          canManage
            ? [{ label: 'システム管理', page: 'submenu-admin' }, { label: 'リリース履歴' }]
            : [{ label: 'リリース履歴' }]
        }
      />
      <main className="mx-auto w-full max-w-5xl px-6 py-8">
        {/* 管理者は システム管理 サブメニューへ、一般ユーザーはホームへ戻る。 */}
        <BackButton onClick={canManage ? goBackToParent : () => navigate('home')} />

        <ResultCard
          title={loading ? undefined : 'リリース履歴'}
          subtitle={
            loading ? undefined : (
              <Text type="secondary">公開済みの更新内容を新しいリリース日順に表示します。</Text>
            )
          }
        >
          {!loading && isDeployAdmin ? (
            <div
              className={`relative mb-4 overflow-hidden rounded-xl border transition-colors duration-300 ${
                maintenanceEnabled
                  ? 'border-rose-200 bg-gradient-to-r from-rose-50 via-white to-rose-50/70'
                  : 'border-emerald-200/70 bg-gradient-to-r from-emerald-50/80 via-white to-white'
              }`}
            >
              {/* 左端のステートバー */}
              <span
                aria-hidden
                className={`absolute inset-y-0 left-0 w-1 transition-colors duration-300 ${
                  maintenanceEnabled ? 'bg-rose-500' : 'bg-emerald-500'
                }`}
              />
              {/* メンテナンス中のみ: ハザードストライプの薄テクスチャ */}
              {maintenanceEnabled ? (
                <span aria-hidden className="maintenance-stripes pointer-events-none absolute inset-0 opacity-[0.05]" />
              ) : null}

              <div className="relative flex flex-wrap items-center gap-x-4 gap-y-3 py-4 pl-5 pr-5">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors duration-300 ${
                    maintenanceEnabled ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600'
                  }`}
                >
                  <ToolOutlined className="text-lg" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold tracking-wide text-gray-800">メンテナンスモード</span>
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        maintenanceEnabled ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      <span className="relative flex h-2 w-2">
                        <span
                          className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
                            maintenanceEnabled ? 'bg-rose-400' : 'bg-emerald-400'
                          }`}
                        />
                        <span
                          className={`relative inline-flex h-2 w-2 rounded-full ${
                            maintenanceEnabled ? 'bg-rose-500' : 'bg-emerald-500'
                          }`}
                        />
                      </span>
                      {maintenanceEnabled ? 'メンテナンス中' : '稼働中'}
                    </span>
                  </div>
                  <p className="mb-0 mt-0.5 text-xs leading-5 text-gray-500">
                    {maintenanceEnabled
                      ? 'deploy 管理者以外のアクセスを遮断しています。リリース完了後に解除してください。'
                      : '開始すると一般ユーザーは強制ログアウトされ、ログインできなくなります。リリース作業の直前に開始してください。'}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2.5">
                  <span
                    className={`text-xs font-medium ${maintenanceEnabled ? 'text-rose-600' : 'text-gray-400'}`}
                  >
                    {maintenanceEnabled ? 'メンテナンスを解除' : 'メンテナンスを開始'}
                  </span>
                  <Switch
                    checked={maintenanceEnabled}
                    loading={maintenanceSaving}
                    onChange={(next) => handleToggleMaintenance(next)}
                    aria-label="メンテナンスモード切替"
                    className="[&.ant-switch-checked]:!bg-rose-600 [&.ant-switch-checked:hover]:!bg-rose-500"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {!loading && canManage ? (
            <div className="mb-4">
              <button type="button" onClick={openCreate} className={demoButtonClass.primarySm}>
                新規作成
              </button>
            </div>
          ) : null}

          {error ? <Alert className="mb-4" type="error" showIcon message={error} /> : null}
          {loading ? (
            <CardLoadingState />
          ) : rows.length === 0 ? (
            <EmptyState title="リリース履歴がありません" description="公開中のリリース履歴はまだ登録されていません。" />
          ) : (
            <>
              <Collapse
                accordion={false}
                items={collapseItems}
                className="release-history-collapse bg-white"
              />
              <Pagination
                currentPage={safePage}
                totalPages={totalPages}
                total={rows.length}
                rangeFrom={rangeFrom}
                rangeTo={rangeTo}
                onPageChange={(next) => setPage(next)}
                pageSize={pageSize}
                onPageSizeChange={(next) => {
                  setPageSize(next)
                  setPage(1)
                }}
              />
            </>
          )}
        </ResultCard>
      </main>

      <Modal
        title={editing ? 'リリース履歴を編集' : 'リリース履歴を新規作成'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText="保存"
        cancelText="キャンセル"
        width={640}
        destroyOnHidden
        maskClosable={false}
      >
        <Form<FormValues> form={form} layout="vertical" requiredMark={false} className="customer-validation-form mt-2">
          <Form.Item
            name="version"
            label={requiredLabel('バージョン')}
            rules={[
              { required: true, message: 'バージョンは必須です。' },
              { max: 64, message: 'バージョンは64文字以内で入力してください。' },
            ]}
          >
            <Input maxLength={64} placeholder="例: v1.2.0" />
          </Form.Item>

          <Form.Item
            name="title"
            label={requiredLabel('タイトル')}
            rules={[
              { required: true, message: 'タイトルは必須です。' },
              { max: 256, message: 'タイトルは256文字以内で入力してください。' },
            ]}
          >
            <Input maxLength={256} />
          </Form.Item>

          <Form.Item
            name="body"
            label={requiredLabel('本文')}
            rules={[{ required: true, message: '本文は必須です。' }]}
          >
            <Input.TextArea rows={6} />
          </Form.Item>

          <Form.Item
            name="releasedAt"
            label={requiredLabel('リリース日')}
            rules={[{ required: true, message: 'リリース日を選択してください。' }]}
          >
            <DatePicker style={{ width: 200 }} format="YYYY-MM-DD" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default ReleaseHistory
